import React, { ComponentProps, ReactElement } from 'react';

import { safePolygon } from '@floating-ui/react';
import PropTypes from 'prop-types';

import { noop, optionalPropType } from '@adsk/alloy-react-helpers';

import MenuItem from './MenuItem';
import useOverlayMenuItem from './useOverlayMenuItem';
import { menuCategoryShape } from '../helpers/propTypes';
import { MenuCategoryType, MenuItemType, SharedProps } from '../helpers/types';
import Menu from '../Menu';

const defaultRenderItem = (props: ComponentProps<typeof MenuItem>) => (
  <MenuItem {...props} />
);

type OverlayMenuItemProps<TKey extends string | number> = Omit<
  ComponentProps<typeof MenuItem>,
  'nested' | 'onChange' | 'value' | 'defaultValue' | 'as' | 'label'
> &
  SharedProps<TKey> & {
    itemKey: TKey;
    nested?: MenuItemType<TKey>['nested'];
    category?: MenuCategoryType<TKey>;
    onClose?: () => void;
    renderItem?: (props: ComponentProps<typeof MenuItem>) => ReactElement;
    /* Options that modify how the safe polygon for nested menus behaves (see floating-ui documentation https://floating-ui.com/docs/shift) */
    safePolygonArgs?: Parameters<typeof safePolygon>[0];
  };

/** An OverlayMenuItem component which handles nested items */
const OverlayMenuItem = <TKey extends string | number>({
  itemKey,
  nested,
  category,
  defaultValue,
  value = [],
  onChange = noop,
  onClose,
  onClick,
  renderItem = defaultRenderItem,
  safePolygonArgs,
  style,
  ...rest
}: OverlayMenuItemProps<TKey>) => {
  const { isOpen, getOverlayMenuItemProps, getNestedMenuProps } =
    useOverlayMenuItem({
      placement: 'right-start',
      disabled: rest.disabled,
      nested,
      itemKey,
      category,
      onClick,
      onChange,
      onClose,
      value,
      safePolygonArgs,
    });

  return renderItem({
    style,
    ...getOverlayMenuItemProps(rest),
    'data-testid': `menu-item--${itemKey}`,
    nested: !!nested,
    showSelection: category?.showSelection,
    children:
      nested && isOpen ? (
        <Menu
          {...getNestedMenuProps()}
          options={nested}
          defaultValue={defaultValue}
          value={value}
          onChange={onChange}
          onClose={onClose}
        />
      ) : undefined,
  });
};

OverlayMenuItem.displayName = 'MenuItemWrapper';

OverlayMenuItem.propTypes = {
  ...MenuItem.propTypes,
  /** Item key for the option has to be unique among all catagories */
  itemKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /** The category of this item */
  category: menuCategoryShape,
  /** nested menu items */
  nested: PropTypes.array,
  /** Pre selected keys */
  defaultValue: optionalPropType(
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    ),
  ),
  /** Value for controlled usage */
  value: optionalPropType(
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    ),
  ),
  /** Handle for item click event */
  onChange: PropTypes.func,
  /** Handle closing of the menu */
  onClose: PropTypes.func,
  /** render custom menu item */
  renderItem: PropTypes.elementType,
};

export default OverlayMenuItem as <TKey extends string | number>(
  p: OverlayMenuItemProps<TKey>,
) => ReactElement;
